import { useState, useCallback, useRef } from 'react';
import api from '../lib/api';
import { LOBBYING_API_TIMEOUT_MS } from '../components/lobbying/constants';
import type {
  LobbyScope, LobbyGroupBy, LobbyGroupRow, LobbyFlatRow, LobbySummary,
} from '../types/lobbying';

const PAGE_SIZE = 100;
const DEFAULT_GROUP_SORT = 'records';
const DEFAULT_FLAT_SORT = 'report_date';

export type SortDir = 'asc' | 'desc';

export interface LobbyRecordsState {
  rows: (LobbyGroupRow | LobbyFlatRow)[];
  total: number;
  grouped: boolean;
  groupBy: LobbyGroupBy;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  summary: LobbySummary | null;
  loaded: boolean;
  sortBy: string;
  sortDir: SortDir;
}

export interface UseLobbyRecordsReturn extends LobbyRecordsState {
  load: (scope: LobbyScope, groupBy?: LobbyGroupBy) => Promise<void>;
  setGroupBy: (groupBy: LobbyGroupBy) => void;
  setSort: (field: string, dir: SortDir) => void;
  showAll: () => Promise<void>;
  /** Fetch the flat records inside a group (for inline expansion), reusing the current scope. */
  fetchRecords: (extra: Record<string, string>) => Promise<LobbyFlatRow[]>;
  reset: () => void;
}

function buildParams(
  scope: LobbyScope,
  groupBy: LobbyGroupBy,
  extra: { offset: number; limit: number; sortBy: string; sortDir: SortDir },
): URLSearchParams {
  const p = new URLSearchParams();
  p.set('offset', String(extra.offset));
  p.set('limit', String(extra.limit));
  p.set('sort_by', extra.sortBy);
  p.set('sort_order', extra.sortDir);
  if (scope.lobbyist_id != null && scope.lobbyist_id !== '') p.set('lobbyist_id', String(scope.lobbyist_id));
  if (scope.bill_number) p.set('bill_number', scope.bill_number);
  if (scope.client_name) p.set('client_name', scope.client_name);
  if (scope.position) p.set('position', scope.position);
  if (scope.fiscal_year) p.set('fiscal_year', scope.fiscal_year);
  if (scope.search) p.set('search', scope.search);
  if (scope.start_date) p.set('start_date', scope.start_date);
  if (scope.end_date) p.set('end_date', scope.end_date);
  if (groupBy && groupBy !== 'none') p.set('group_by', groupBy);
  return p;
}

const initialState = (): LobbyRecordsState => ({
  rows: [], total: 0, grouped: false, groupBy: 'none', hasMore: false,
  loading: false, error: null, summary: null, loaded: false,
  sortBy: DEFAULT_GROUP_SORT, sortDir: 'desc',
});

/**
 * Data hook for the Lobbying Explorer. One endpoint (`/lobbying/records`) serves
 * both grouped and flat views; toggling group_by or sort re-fetches server-side,
 * and expanding a group lazily fetches its flat children via fetchRecords.
 * Mirrors useCFEntity, minus the money-specific aggregates.
 */
export function useLobbyRecords(): UseLobbyRecordsReturn {
  const [state, setState] = useState<LobbyRecordsState>(initialState);

  const scopeRef = useRef<LobbyScope>({});
  const groupByRef = useRef<LobbyGroupBy>('none');
  const sortByRef = useRef<string>(DEFAULT_GROUP_SORT);
  const sortDirRef = useRef<SortDir>('desc');

  const fetchPage = useCallback(async (
    scope: LobbyScope, groupBy: LobbyGroupBy, offset: number, limit: number, sortBy: string, sortDir: SortDir,
  ) => {
    const params = buildParams(scope, groupBy, { offset, limit, sortBy, sortDir });
    const res = await api.get(`/lobbying/records?${params.toString()}`, { timeout: LOBBYING_API_TIMEOUT_MS });
    if (!res.data?.success) throw new Error(res.data?.error || 'Request failed');
    return res.data.data;
  }, []);

  const runLoad = useCallback(async (scope: LobbyScope, groupBy: LobbyGroupBy, sortBy: string, sortDir: SortDir) => {
    scopeRef.current = scope;
    groupByRef.current = groupBy;
    sortByRef.current = sortBy;
    sortDirRef.current = sortDir;
    setState(s => ({ ...s, loading: true, error: null, sortBy, sortDir }));
    try {
      const data = await fetchPage(scope, groupBy, 0, PAGE_SIZE, sortBy, sortDir);
      setState({
        rows: data.records || [],
        total: data.total || 0,
        grouped: !!data.grouped,
        groupBy: data.groupBy || 'none',
        hasMore: !!data.hasMore,
        loading: false,
        error: null,
        summary: data.summary || null,
        loaded: true,
        sortBy,
        sortDir,
      });
    } catch (err: any) {
      setState(s => ({
        ...s,
        loading: false,
        loaded: true,
        rows: [],
        total: 0,
        error: err?.response?.data?.error || err?.message || 'Failed to load lobbying records',
      }));
    }
  }, [fetchPage]);

  const load = useCallback((scope: LobbyScope, groupBy: LobbyGroupBy = 'none') => {
    const defaultSort = groupBy === 'none' ? DEFAULT_FLAT_SORT : DEFAULT_GROUP_SORT;
    return runLoad(scope, groupBy, defaultSort, 'desc');
  }, [runLoad]);

  const setGroupBy = useCallback((groupBy: LobbyGroupBy) => {
    const defaultSort = groupBy === 'none' ? DEFAULT_FLAT_SORT : DEFAULT_GROUP_SORT;
    void runLoad(scopeRef.current, groupBy, defaultSort, 'desc');
  }, [runLoad]);

  const setSort = useCallback((field: string, dir: SortDir) => {
    void runLoad(scopeRef.current, groupByRef.current, field, dir);
  }, [runLoad]);

  const showAll = useCallback(async () => {
    if (state.total <= 0 || state.rows.length >= state.total) return;
    setState(s => ({ ...s, loading: true }));
    try {
      const data = await fetchPage(scopeRef.current, groupByRef.current, 0, state.total, sortByRef.current, sortDirRef.current);
      setState(s => ({ ...s, rows: data.records || [], hasMore: false, loading: false }));
    } catch (err: any) {
      setState(s => ({ ...s, loading: false, error: err?.message || 'Failed to load all rows' }));
    }
  }, [fetchPage, state.total, state.rows.length]);

  const fetchRecords = useCallback(async (extra: Record<string, string>): Promise<LobbyFlatRow[]> => {
    const params = buildParams(scopeRef.current, 'none', { offset: 0, limit: 500, sortBy: DEFAULT_FLAT_SORT, sortDir: 'desc' });
    Object.entries(extra).forEach(([k, v]) => params.set(k, v));
    const res = await api.get(`/lobbying/records?${params.toString()}`, { timeout: LOBBYING_API_TIMEOUT_MS });
    if (!res.data?.success) return [];
    return res.data.data?.records || [];
  }, []);

  const reset = useCallback(() => {
    scopeRef.current = {};
    groupByRef.current = 'none';
    sortByRef.current = DEFAULT_GROUP_SORT;
    sortDirRef.current = 'desc';
    setState(initialState());
  }, []);

  return { ...state, load, setGroupBy, setSort, showAll, fetchRecords, reset };
}
